/**
 * WCC360 v2 — durable persistence for an assessment run.
 *
 * Three rules govern this file:
 *
 *  1. An answer is never lost. Every answer goes to localStorage FIRST and
 *     to the network second. A failed write stays in the outbox and is
 *     retried; it is never dropped and never blocks the UI.
 *  2. The UI never waits on the network. Every export here is fire-and-
 *     forget from the caller's point of view.
 *  3. Nothing here does psychometric math. This module moves rows.
 *
 * CANON NOTE — why answers land in `assessment_v2_responses` and not
 * directly in `learner_evidence`:
 *   `learner_evidence.item_code` is a foreign key into the sealed Item Bank,
 *   whose `item_type` vocabulary is exactly {Likert-5, Select}. v2 has no
 *   Likert at all — it has sliders, workday sorts and tournaments. There is
 *   no honest way to describe a v2 screen as a canonical item today, and
 *   this repository must not mutate a sealed scientific registry. So v2
 *   answers are staged here with their full provenance, and
 *   `promote_v2_evidence(session_id)` replays them into `learner_evidence`
 *   the moment the Command Center ships the item-type extension tracked in
 *   `canon_pending_items` under key `v2-item-types`.
 */
import { supabase } from "@/integrations/supabase/client";
import { clearPersistedRunCache } from "./persistedSnapshot";
import type { Band } from "./band";
import { bandParam } from "./band";
import { cloudStorage } from "@/lib/cloudStore";

export const V2_ITEM_VERSION = "2.0.0-v2";
export const V2_ENGINE_VERSION = "3.4.0";
export const V2_RUNTIME_VERSION = "1.0.0";
/** The item bank version the canonical pipeline is pinned to. */
export const CANON_ITEM_BANK_VERSION = "1.0.0";

/** DB-side band value. The check constraint accepts exactly these three. */
export function bandDbValue(band: Band): "12-14" | "15-18" | "18+" {
  return bandParam(band) as "12-14" | "15-18" | "18+";
}

// ---------------------------------------------------------------- observability

/**
 * Persistence must never break the UI, but it must never fail *silently*
 * either — a swallowed error here means a learner's answers vanish with no
 * trace. Everything that goes wrong is logged under one prefix and kept in
 * a small in-memory ring the E2E walk can read.
 */
export interface PersistenceProblem {
  at: string;
  where: string;
  message: string;
}
const problems: PersistenceProblem[] = [];

export function persistenceProblems(): PersistenceProblem[] {
  return [...problems];
}

function report(where: string, err: unknown) {
  const message = err instanceof Error ? err.message : typeof err === "string" ? err : JSON.stringify(err);
  const entry = { at: new Date().toISOString(), where, message };
  problems.push(entry);
  if (problems.length > 50) problems.shift();
  // eslint-disable-next-line no-console
  console.error(`[wcc360.v2.persistence] ${where}: ${message}`);
  try {
    (window as unknown as { __wcc360Persistence?: PersistenceProblem[] }).__wcc360Persistence = problems;
  } catch { /* ignore */ }
}

// ------------------------------------------------------------- save status feed
/**
 * A learner should never have to wonder whether their answers made it. The
 * walk subscribes to this and shows one honest line: queued on this device,
 * writing, saved, or retrying. Presentation only — nothing here scores.
 */
export type SaveState = "idle" | "queued" | "saving" | "saved" | "retrying";

export interface SaveStatus {
  state: SaveState;
  pending: number;
  /** ISO time of the last successful write to the database. */
  savedAt: string | null;
  /** True once the run has been marked complete in the database. */
  completed: boolean;
}

let saveStatus: SaveStatus = { state: "idle", pending: 0, savedAt: null, completed: false };
const saveListeners = new Set<(s: SaveStatus) => void>();

export function getSaveStatus(): SaveStatus { return saveStatus; }

export function subscribeSaveStatus(fn: (s: SaveStatus) => void): () => void {
  saveListeners.add(fn);
  fn(saveStatus);
  return () => { saveListeners.delete(fn); };
}

function setSaveStatus(patch: Partial<SaveStatus>) {
  saveStatus = { ...saveStatus, ...patch };
  saveListeners.forEach((fn) => { try { fn(saveStatus); } catch { /* ignore */ } });
}

// ---------------------------------------------------------------- storage keys

function sessionKey(band: Band) { return `wcc360.v2.session.${bandParam(band)}`; }
function outboxKey(band: Band) { return `wcc360.v2.outbox.${bandParam(band)}`; }
function assessmentKey(band: Band) { return `wcc360.v2.assessment.${bandParam(band)}`; }


function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = cloudStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch { return fallback; }
}
function writeJson(key: string, value: unknown) {
  try { cloudStorage.setItem(key, JSON.stringify(value)); } catch (e) { report("cloudStore.write", e); }
}


// ------------------------------------------------------------------- the queue

export interface OutboxRow {
  session_id: string;
  learner_id: string;
  band: "12-14" | "15-18" | "18+";
  part: "part1" | "part2";
  screen_id: string;
  raw_value: unknown;
  slot_writes: Record<string, unknown>;
  sequence: number | null;
  item_version: string;
  engine_version: string;
  observed_at: string;
}

/** Rows waiting to reach the database, oldest first. */
export function outboxRows(band: Band): OutboxRow[] {
  return readJson<OutboxRow[]>(outboxKey(band), []);
}
export function outboxSize(band: Band): number {
  return outboxRows(band).length;
}

/**
 * Replace an existing entry for the same screen — never double-write.
 * Matching is on the screen alone: a row queued before the session existed
 * carries `session_id: ""` and must still be replaced by a later answer to
 * the same screen.
 */
function pushOutbox(band: Band, row: OutboxRow) {
  const rows = outboxRows(band).filter((r) => r.screen_id !== row.screen_id);
  rows.push(row);
  writeJson(outboxKey(band), rows);
}


// ----------------------------------------------------------------- the session

/** The session id for this band's run, if one has been created. */
export function currentSessionId(band: Band): string | null {
  return readJson<string | null>(sessionKey(band), null);
}

let sessionInFlight: Promise<string | null> | null = null;

/**
 * Create (or reuse) the `runtime_sessions` row for this run.
 *
 * Reuse order, so one assessment run is one session row no matter how many
 * times the tree remounts, the tab refreshes or the learner navigates back:
 *   1. the id already on this device for this band;
 *   2. an open `in_progress` v2 session for this learner and band in the
 *      database (covers cleared storage, a second device, a hard reload);
 *   3. only then insert a new one.
 *
 * Signed-out learners get `null` and keep going entirely on-device — the
 * assessment is not gated behind auth. The next call after sign-in creates
 * the session and the outbox drains into it.
 */
export async function ensureSession(band: Band): Promise<string | null> {
  const existing = currentSessionId(band);
  if (existing) return existing;
  if (sessionInFlight) return sessionInFlight;

  sessionInFlight = (async () => {
    try {
      const { data: auth, error: authError } = await supabase.auth.getUser();
      const learner_id = auth?.user?.id;
      if (!learner_id) {
        if (authError) report("ensureSession.getUser", authError);
        return null;
      }

      // The learner-facing record of "an assessment was taken". Separate
      // from the runtime session: History, XP and the dashboard read this.
      void ensureAssessmentRecord(band, learner_id);


      // 2. Adopt an open v2 session for this learner and band.
      const { data: open, error: openError } = await supabase
        .from("runtime_sessions")
        .select("id, metadata")
        .eq("learner_id", learner_id)
        .eq("status", "in_progress")
        .order("started_at", { ascending: false })
        .limit(20);
      if (openError) report("ensureSession.select", openError);
      const adopted = (open ?? []).find((row) => {
        const meta = (row.metadata ?? {}) as Record<string, unknown>;
        return meta.source === "assessment-v2" && meta.band === bandDbValue(band);
      });
      if (adopted?.id) {
        writeJson(sessionKey(band), adopted.id);
        return adopted.id as string;
      }

      const { data, error } = await supabase
        .from("runtime_sessions")
        .insert({
          learner_id,
          engine_version: V2_ENGINE_VERSION,
          item_bank_version: CANON_ITEM_BANK_VERSION,
          runtime_version: V2_RUNTIME_VERSION,
          seed: randomHex(16),
          status: "in_progress",
          metadata: {
            source: "assessment-v2",
            band: bandDbValue(band),
            v2_item_version: V2_ITEM_VERSION,
          },
        })
        .select("id")
        .single();
      if (error || !data) {
        report("ensureSession.insert", error ?? "no row returned");
        return null;
      }
      writeJson(sessionKey(band), data.id);
      return data.id as string;
    } catch (e) {
      report("ensureSession", e);
      return null;
    }
    finally { sessionInFlight = null; }
  })();

  return sessionInFlight;
}


function randomHex(bytes = 16): string {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return Array.from(buf).map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * The `assessments` row for this run — what the dashboard, History &
 * milestones list and XP triggers read. v2 answers live in
 * `assessment_v2_responses`, but without this row a completed run is
 * invisible to every learner-facing surface.
 *
 * Reuse order mirrors the session: local id → the learner's open
 * `in_progress` row → insert. Cadence and single-take rules live in the
 * database trigger; a rejection is reported, never thrown at the UI.
 */
export function currentAssessmentId(band: Band): string | null {
  return readJson<string | null>(assessmentKey(band), null);
}

let assessmentInFlight: Promise<string | null> | null = null;

export async function ensureAssessmentRecord(
  band: Band,
  learnerId: string,
): Promise<string | null> {
  const existing = currentAssessmentId(band);
  if (existing) return existing;
  if (assessmentInFlight) return assessmentInFlight;

  assessmentInFlight = (async () => {
    try {
      // Adopt an open row. Prefer one for this band; fall back to any open
      // row, because the cadence trigger refuses a second insert while one
      // is still in progress — adopting it is the only way forward.
      const { data: open, error: openError } = await supabase
        .from("assessments")
        .select("id, age_group")
        .eq("user_id", learnerId)
        .eq("status", "in_progress")
        .order("started_at", { ascending: false })
        .limit(10);
      if (openError) report("ensureAssessment.select", openError);
      const rows = open ?? [];
      const adopted =
        rows.find((r) => r.age_group === bandDbValue(band))?.id ?? rows[0]?.id ?? null;
      if (adopted) {
        writeJson(assessmentKey(band), adopted);
        return adopted as string;
      }

      const { data, error } = await supabase
        .from("assessments")
        .insert({
          user_id: learnerId,
          age_group: bandDbValue(band),
          status: "in_progress",
          bank_version: V2_ITEM_VERSION,
          algo_version: V2_ENGINE_VERSION,
        })
        .select("id")
        .single();
      if (error || !data) {
        // A cadence or single-take rejection is a product rule, not a data
        // loss: the run itself lives in `runtime_sessions` +
        // `assessment_v2_responses`, and history reads those too. Record it
        // and carry on rather than blocking the walk.
        report("ensureAssessment.insert", error ?? "no row returned");
        return null;
      }
      writeJson(assessmentKey(band), data.id);
      return data.id as string;
    } catch (e) {
      report("ensureAssessment", e);
      return null;
    } finally { assessmentInFlight = null; }
  })();

  return assessmentInFlight;
}


/** Forget the session so a fresh run starts a fresh session. */
export function clearSession(band: Band) {
  try {
    cloudStorage.removeItem(sessionKey(band));
    cloudStorage.removeItem(outboxKey(band));
    cloudStorage.removeItem(assessmentKey(band));
  } catch { /* ignore */ }
}

// -------------------------------------------------------------------- recording

export interface RecordArgs {
  band: Band;
  part: "part1" | "part2";
  screenId: string;
  rawValue: unknown;
  slotWrites?: Record<string, unknown>;
  sequence?: number | null;
}

/**
 * Record one answer. Fully synchronous from the caller's point of view: the
 * answer is in the outbox before this function returns, so a screen advance
 * never waits on auth or the network, and an answer given before the
 * session exists (or while signed out) is still kept and sent later.
 *
 * Rows are stamped with the session and learner at flush time — `""` means
 * "not yet known".
 */
export function recordAnswer(args: RecordArgs): void {
  const { band, part, screenId, rawValue, slotWrites, sequence } = args;

  pushOutbox(band, {
    session_id: currentSessionId(band) ?? "",
    learner_id: "",
    band: bandDbValue(band),
    part,
    screen_id: screenId,
    raw_value: { value: rawValue as never },
    slot_writes: slotWrites ?? {},
    sequence: sequence ?? null,
    item_version: V2_ITEM_VERSION,
    engine_version: V2_ENGINE_VERSION,
    observed_at: new Date().toISOString(),
  });
  setSaveStatus({ state: "queued", pending: outboxSize(band), completed: false });
  scheduleFlush(band);
}

// --------------------------------------------------------------------- flushing

const RETRY_MS = [0, 1500, 5000, 15000, 60000];
const flushTimers = new Map<string, number>();
const flushAttempts = new Map<string, number>();
let flushing = false;

function scheduleFlush(band: Band) {
  const key = bandParam(band);
  if (flushTimers.has(key)) return;
  const attempt = flushAttempts.get(key) ?? 0;
  const delay = RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)];
  const t = window.setTimeout(() => {
    flushTimers.delete(key);
    void flushOutbox(band);
  }, delay);
  flushTimers.set(key, t);
}

function retryLater(band: Band) {
  const key = bandParam(band);
  flushAttempts.set(key, (flushAttempts.get(key) ?? 0) + 1);
  scheduleFlush(band);
}

/**
 * Push everything pending to the database. Rows that land are removed;
 * rows that fail stay put and are retried with backoff.
 *
 * Returns the number of rows still pending afterwards, so callers can show
 * "saved on this device, syncing" honestly.
 */
export async function flushOutbox(band: Band): Promise<number> {
  const key = bandParam(band);
  if (flushing) return outboxSize(band);
  const rows = outboxRows(band);
  if (rows.length === 0) { flushAttempts.set(key, 0); return 0; }

  flushing = true;
  setSaveStatus({ state: "saving", pending: rows.length });
  try {
    const session_id = await ensureSession(band);
    if (!session_id) {
      // Signed out, or the session could not be created. Answers stay in the
      // outbox and go out on the next attempt.
      retryLater(band);
      setSaveStatus({ state: "retrying", pending: rows.length });
      return rows.length;
    }
    const { data: auth } = await supabase.auth.getUser();
    const learner_id = auth?.user?.id;
    if (!learner_id) {
      retryLater(band);
      setSaveStatus({ state: "retrying", pending: rows.length });
      return rows.length;
    }

    const { error } = await supabase
      .from("assessment_v2_responses")
      .upsert(
        rows.map((r) => ({
          session_id: r.session_id || session_id,
          learner_id: r.learner_id || learner_id,
          band: r.band,
          part: r.part,
          screen_id: r.screen_id,
          raw_value: r.raw_value as never,
          slot_writes: r.slot_writes as never,
          sequence: r.sequence,
          item_version: r.item_version,
          engine_version: r.engine_version,
          observed_at: r.observed_at,
        })),
        { onConflict: "session_id,screen_id" },
      );

    if (error) {
      report("flushOutbox.upsert", error);
      retryLater(band);
      setSaveStatus({ state: "retrying", pending: rows.length });
      return rows.length;
    }

    // Drop exactly what we sent. Anything queued meanwhile survives.
    const sent = new Set(rows.map((r) => r.screen_id));
    const remaining = outboxRows(band).filter((r) => !sent.has(r.screen_id));
    writeJson(outboxKey(band), remaining);
    flushAttempts.set(key, 0);
    setSaveStatus({
      state: remaining.length > 0 ? "queued" : "saved",
      pending: remaining.length,
      savedAt: new Date().toISOString(),
    });
    if (remaining.length > 0) scheduleFlush(band);
    return remaining.length;
  } catch (e) {
    report("flushOutbox", e);
    retryLater(band);
    setSaveStatus({ state: "retrying", pending: outboxSize(band) });
    return outboxSize(band);
  } finally {
    flushing = false;
  }
}

/**
 * Close the run: mark the runtime session complete AND mark the learner's
 * `assessments` row completed, so History, XP and the dashboard see it.
 * Best-effort — never blocks results.
 */
export async function completeSession(band: Band, itemsResponded: number): Promise<string | null> {
  const session_id = currentSessionId(band);
  await flushOutbox(band);
  const completed_at = new Date().toISOString();
  // A newly sealed run must invalidate any cached replay.
  clearPersistedRunCache();


  if (session_id) {
    try {
      const { error } = await supabase
        .from("runtime_sessions")
        .update({
          status: "completed",
          completed_at,
          items_responded: itemsResponded,
          items_delivered: itemsResponded,
        })
        .eq("id", session_id);
      if (error) report("completeSession.update", error);
    } catch (e) { report("completeSession", e); }
  }

  try {
    const { data: auth } = await supabase.auth.getUser();
    const learner_id = auth?.user?.id;
    if (learner_id) {
      const assessment_id = await ensureAssessmentRecord(band, learner_id);
      if (assessment_id) {
        const { error } = await supabase
          .from("assessments")
          .update({ status: "completed", completed_at })
          .eq("id", assessment_id);
        if (error) report("completeAssessment.update", error);
        else {
          // A completed run is finished — the next run starts a new row.
          try { cloudStorage.removeItem(assessmentKey(band)); } catch { /* ignore */ }
        }
      }
    }
  } catch (e) { report("completeAssessment", e); }

  const pending = outboxSize(band);
  setSaveStatus({
    state: pending > 0 ? "retrying" : "saved",
    pending,
    savedAt: pending > 0 ? saveStatus.savedAt : completed_at,
    completed: pending === 0,
  });

  return session_id;

}


// -------------------------------------------------- canonical pipeline gating

/**
 * How many v2 screens can currently be promoted into `learner_evidence`.
 *
 * Zero means the Command Center has not yet minted canonical items for the
 * v2 interaction types, so starting the engine pipeline would fail at the
 * `evidence` stage for every learner. We check instead of guessing.
 */
export async function promotableScreenCount(): Promise<number> {
  try {
    const { data, error } = await (supabase.rpc as any)("v2_promotable_screen_count");
    if (error) return 0;
    return Number(data ?? 0);
  } catch { return 0; }
}

// ------------------------------------------------------------- reconciliation

export interface ReconciliationInput {
  band: Band;
  sessionId: string;
  clientTop10: string[];
  canonicalTop10: string[];
  clientArchetype: string | null;
  canonicalArchetype: string | null;
  details?: Record<string, unknown>;
}

/** Set overlap of the two top-10 lists, 0..1. */
export function jaccard(a: string[], b: string[]): number {
  const sa = new Set(a);
  const sb = new Set(b);
  if (sa.size === 0 && sb.size === 0) return 1;
  let inter = 0;
  for (const x of sa) if (sb.has(x)) inter++;
  const union = sa.size + sb.size - inter;
  return union === 0 ? 0 : inter / union;
}

/**
 * Spearman rank correlation over the ids present in BOTH lists. Fewer than
 * two shared ids means there is nothing to correlate, so we return null
 * rather than a fabricated 0.
 */
export function rankCorrelation(a: string[], b: string[]): number | null {
  const shared = a.filter((id) => b.includes(id));
  const n = shared.length;
  if (n < 2) return null;
  let sumD2 = 0;
  for (const id of shared) {
    const d = a.indexOf(id) - b.indexOf(id);
    sumD2 += d * d;
  }
  return 1 - (6 * sumD2) / (n * (n * n - 1));
}

/** Write the client-vs-canonical divergence row. Once per session. */
export async function recordReconciliation(input: ReconciliationInput): Promise<void> {
  try {
    const { data: auth } = await supabase.auth.getUser();
    const learner_id = auth?.user?.id;
    if (!learner_id) return;
    await supabase.from("assessment_v2_reconciliation").upsert(
      {
        learner_id,
        session_id: input.sessionId,
        band: bandDbValue(input.band),
        client_top10: input.clientTop10.slice(0, 10),
        canonical_top10: input.canonicalTop10.slice(0, 10),
        overlap: jaccard(input.clientTop10.slice(0, 10), input.canonicalTop10.slice(0, 10)),
        rank_correlation: rankCorrelation(
          input.clientTop10.slice(0, 10),
          input.canonicalTop10.slice(0, 10),
        ),
        client_archetype: input.clientArchetype,
        canonical_archetype: input.canonicalArchetype,
        archetype_match:
          input.clientArchetype != null && input.canonicalArchetype != null
            ? input.clientArchetype === input.canonicalArchetype
            : null,
        details: (input.details ?? {}) as never,
      },
      { onConflict: "session_id", ignoreDuplicates: true },
    );
  } catch { /* divergence logging must never break a result page */ }
}

/* ------------------------------------------------------------------ *
 * Global drain
 *
 * A learner can leave the flow mid-run — closed tab, dead signal, a tap on
 * the logo. Their answers sit in the localStorage outbox until something
 * sends them. Previously only the assessment tree drained it, so leaving the
 * flow and never returning meant the answers never landed. This drains every
 * band from anywhere in the app, on load, on reconnect and on tab focus.
 * ------------------------------------------------------------------ */

const ALL_BANDS: Band[] = ["18plus", "15to18", "12to14"];

/** Push every band's stranded answers. Safe to call often; no-ops when empty. */
export async function drainAllOutboxes(): Promise<void> {
  for (const band of ALL_BANDS) {
    if (!outboxSize(band)) continue;
    try {
      await flushOutbox(band);
    } catch (e) {
      report("drainAll", e);
    }
  }
}

let drainWired = false;

/** Wire the drain to load, reconnect and tab focus. Call once, from the app root. */
export function startOutboxDrain(): () => void {
  if (drainWired || typeof window === "undefined") return () => undefined;
  drainWired = true;
  const run = () => { void drainAllOutboxes(); };
  const onVisible = () => { if (document.visibilityState === "visible") run(); };
  window.addEventListener("online", run);
  document.addEventListener("visibilitychange", onVisible);
  run();
  return () => {
    window.removeEventListener("online", run);
    document.removeEventListener("visibilitychange", onVisible);
    drainWired = false;
  };
}
